import { SummarizedProduct } from '@shop/models/src/product';
import tracking, { makeEvent } from '.';
import { useInView } from 'react-intersection-observer';
import { ProductState, State as PropsState } from '@shop/utils/product-state';
import { Cart } from '@shop/models/src/cart';
import { storage } from '@/utils/storage';
import { User } from '@shop/models/src/identity';
import { getOrderState } from '@shop/utils/order-state';

const noop = (v) => v;

type Item = Pick<
  SummarizedProduct,
  'id' | 'title' | 'price' | 'discount' | 'tags'
> & { index: number; quantity?: number; props?: PropsState };

function logger() {
  const log =
    (name: string, format = noop) =>
    (
      items: Item[],
      meta?: { item_list_name: string; item_list_id: string },
    ) => {
      tracking.record(
        makeEvent(name, {
          ecommerce: format({
            items: items.map((item) => ({
              item_id: item.id,
              item_name: item.title,
              price: item.price,
              discount: item.discount.price,
              item_brand: 'pintora',
              ...item.tags.slice(0, 5).reduce((a, c, i) => {
                a[`item_category${i + 1}`] = c;
                return a;
              }, {}),
              index: item.index,
              quantity: item.quantity,
              ...item.props,
            })),
            value: items.reduce((a, c) => {
              a += (c.price - c.discount.price) * (c.quantity || 1);
              return a;
            }, 0),
            item_list_name: meta?.item_list_name,
            item_list_id: meta?.item_list_id,
            currency: 'IRR',
          }),
        }),
      );
    };

  const cart = () =>
    storage.local.cart.get<Cart>().items.map((e, i) => {
      const s = new ProductState(e.product, e.project, e.props);
      return {
        ...e.product,
        ...e,
        price: s.price,
        discount: s.discount,
        quantity: e.qty,
        index: i,
      };
    });

  return {
    selectItem: log('select_item'),
    viewItemList: log('view_item_list'),
    viewItem: log('view_item'),
    addToCart: log('add_to_cart'),
    removeFromCart: log('remove_from_cart'),
    viewCart: () => log('view_cart')(cart()),
    beginCheckout: () => log('begin_checkout')(cart()),
    login() {
      tracking.record(
        makeEvent('login', {
          method: 'otp',
        }),
      );
    },
    addShippingInfo: () => log('add_shipping_info')(cart()),
    purchase: (params) => {
      log('purchase', (p) => ({
        ...p,
        ...params,
      }))([]);
    },
    redirectToIPG: ({ ipg, shipmentMethod, items }) => {
      const s = getOrderState({ items, shipment: shipmentMethod });
      const payload = {
        ipg,
        user_name: storage.local.user.get<User>().name,
        payable_amount: s.payableAmount,
        discount_amount: s.discount,
        shipment_amount: s.shipment?.price,
      };
      tracking.record(makeEvent('redirect_to_ipg', payload));
    },
  };
}

export type GAItemListProps = {
  ga: {
    item_list_id: string;
    item_list_name: string;
  };
};

export function useViewItemListLogger(
  items: SummarizedProduct[],
  ga: GAItemListProps['ga'],
) {
  return useInView({
    onChange: (inView) => {
      if (inView) {
        logger().viewItemList(
          items.map((e, i) => ({
            ...e,
            index: i,
          })),
          ga,
        );
      }
    },
    triggerOnce: true,
  });
}

export default logger();
